import React, { useEffect, useMemo, useRef, useState } from 'react';
import P from 'prop-types';
import {
  setTrk,
  TrkTag,
  TrkElement,
} from '@nerdwallet/nw-client-lib/trk-helper';
import { v4 as uuid } from 'uuid';
import classNames from 'classnames';
import { useSearchParams } from 'next/navigation';
import CtaButton from './lib/cta-button';
import { group, wrapper } from '../../lib/editing-configs';
import UniversalText from '../universal-text/universal-text';
import UniversalContainer from '../universal-container';
import { addWebProtocolIfNeeded } from '../../lib/add-web-protocol';
import styles from './universal-button.module.scss';
import { useCustomButtonColor } from './lib/use-custom-button-color';

const defaultProps = {
  buttonText: 'Button',
  buttonType: 'button',
};

const UniversalButton = ({
  buttonURL,
  buttonText,
  textColorOptions,
  titleColorOptions,
  componentName,
  ctaExtraText,
  alignment,
  outputSample,
  buttonContainer,
  includeContainer,
  customButtonColorValues,
  customButtonActiveColor,
  customButtonHoverColor,
  customButtonTextActiveColor,
  customButtonTextHoverColor,
  ...ctaButtonProps
}) => {
  // Only append trk params if this is not an #anchor link on the
  // current page.
  //
  // @todo in the future we can extract this to a new maybeSetTrk()
  //     util if there are multiple use cases.
  const isAnchorLink = (buttonURL || '').charAt(0) === '#';
  const componentTrkName = componentName || 'universal-button';
  const modifiedUrl = isAnchorLink
    ? buttonURL
    : buttonURL &&
      setTrk({
        url: addWebProtocolIfNeeded(buttonURL),
        params: {
          [TrkTag.location]: componentTrkName,
          [TrkTag.element]: TrkElement.button,
          [TrkTag.copy]: buttonText,
        },
      });
  const nodeId = uuid();
  // useState is used to ensure that the trkId is unique per instance
  // and will only be set/updated when the component initially renders
  const [trkId] = useState(nodeId);

  const buttonProps = useMemo(
    () => ({
      impressionId: trkId,
      ...ctaButtonProps,
      buttonText:
        !buttonText && outputSample ? defaultProps.buttonText : buttonText,
      buttonType:
        !ctaButtonProps?.buttonType ||
        (ctaButtonProps?.buttonText === 'none' && outputSample)
          ? defaultProps.buttonType
          : ctaButtonProps?.buttonType,
    }),
    [buttonText, ctaButtonProps, outputSample, trkId]
  );

  const buttonContainerProps = {
    ...buttonContainer,
    alignment: {
      ...buttonContainer?.alignment,
      type: 'flex',
      direction: 'column',
    },
  };

  const buttonRef = useRef();
  const [disabled, setDisabled] = useState(false);

  const enableValidation =
    (ctaButtonProps?.enableQueryStringFieldValidation &&
      ctaButtonProps?.queryStringFieldValidationName) ||
    false;

  useCustomButtonColor({
    buttonRef,
    customButtonActiveColor,
    customButtonColorValues,
    customButtonHoverColor,
    customButtonTextActiveColor,
    customButtonTextHoverColor,
  });

  const searchParams = useSearchParams();
  const validationQueryParamFound = searchParams.get(
    ctaButtonProps?.queryStringFieldValidationName
  );

  useEffect(() => {
    if (!enableValidation) {
      return;
    }
    setDisabled(!validationQueryParamFound);
  }, [enableValidation, validationQueryParamFound]);

  const isButtonDisabled = enableValidation
    ? disabled || !validationQueryParamFound
    : false;

  return (
    <div
      ref={buttonRef}
      style={{ textAlign: alignment }}
      className={classNames({ [styles.button]: customButtonColorValues })}
    >
      {includeContainer ? (
        <UniversalContainer
          {...buttonContainerProps}
          className={styles.container}
        >
          <CtaButton
            buttonURL={modifiedUrl}
            buttonClass={classNames('cta__button', styles.cta__button)}
            {...buttonProps}
            disabled={isButtonDisabled}
          />
          <UniversalText
            {...ctaExtraText}
            defaultOptions={{ text: { textType: 'text' } }}
          />
        </UniversalContainer>
      ) : (
        <React.Fragment>
          <CtaButton
            buttonURL={modifiedUrl}
            buttonClass="cta__button"
            {...buttonProps}
            disabled={isButtonDisabled}
          />
          <UniversalText
            {...ctaExtraText}
            defaultOptions={{ text: { textType: 'text' } }}
          />
        </React.Fragment>
      )}
    </div>
  );
};

UniversalButton.displayName = 'UniversalButton';

UniversalButton.propTypes = {
  ...CtaButton.propTypes,
  componentName: P.string,
  alignment: P.oneOf(['left', 'center', 'right']),
  children: P.node,
};

UniversalButton.defaultProps = {
  ...CtaButton.defaultProps,
  componentName: 'universal-button',
  alignment: 'left',
  children: null,
};

/**
 * @param {*} componentDocument
 * @param {string} fieldName
 * @param {string} fieldLabel
 * @param {Array<'displayStyle'|'disabled'|'loading'|'loadingMessage'|'alignment'>} allowedControlsList List of controls to be shown in the editor
 * @param {object} options
 * @returns
 */
UniversalButton.getEditingConfig = (
  componentDocument,
  fieldName = 'universalButton',
  fieldLabel = 'Universal Button',
  allowedControlsList = [],
  options = {
    disableGroup: false,
    includeContainer: false,
  }
) => {
  const editorPanelOptions = [
    ...CtaButton.getEditingConfig(
      componentDocument,
      fieldName,
      allowedControlsList
    ),
    ...(options.includeContainer
      ? [
          ...UniversalContainer.getEditingConfig(
            componentDocument,
            'Container',
            ['padding', 'margin', 'alignment', 'width'],
            `${fieldName}.buttonContainer`,
            { disableGroup: true, includeContainer: true }
          ),
        ]
      : []),
  ];
  return [
    options.disableGroup
      ? wrapper(editorPanelOptions)
      : group(fieldLabel, editorPanelOptions),
  ];
};

export default UniversalButton;
